import React from "react";
import {
  Card,
  Checkbox,
  DataTable,
  DataTableColumn,
  FormControlLabel,
  LongTextLabel,
  Table,
  ValueOrPlaceholder,
} from "@sscs/ui";
import {
  getClassName,
  getReleaseDateDisplay,
  humanReadableBytes,
} from "@sscs/utils";
import { QuotaBar } from "Components";
import { ImportableVersion } from "../../types";
import { getSelectionSize } from "../../utils";
import styles from "./importableVersionsSelection.module.scss";

type ImportableVersionsSelectionProps = {
  catalogName: string;
  versions: ImportableVersion[];
  selected: string[];
  onChange: (selected: string[]) => void;
  remainingCapacity: number;
  maxVersions: number;
  emptySubject: string;
};

const ImportableVersionsSelection = ({
  catalogName,
  versions,
  selected,
  onChange,
  remainingCapacity,
  maxVersions,
  emptySubject,
}: ImportableVersionsSelectionProps): React.JSX.Element => {
  const selectedBytes = getSelectionSize(versions, selected);
  const isOverCapacity = selectedBytes > remainingCapacity;
  const selectableCount = Math.min(maxVersions, versions.length);
  const areAllVersionsSelected = selected.length === selectableCount;

  const toggleVersion = (version: string): void => {
    onChange(
      selected.includes(version)
        ? selected.filter((item) => item !== version)
        : [...selected, version],
    );
  };

  const toggleAll = (): void => {
    onChange(
      areAllVersionsSelected
        ? []
        : versions.slice(0, selectableCount).map((item) => item.version),
    );
  };

  const columns: DataTableColumn[] = [
    {
      id: "version",
      width: "100%",
      headerClassName: styles["header-cell"],
      header: (
        <span className={styles["version-header"]}>
          <Checkbox
            size="small"
            checked={areAllVersionsSelected}
            indeterminate={selected.length > 0 && !areAllVersionsSelected}
            onChange={toggleAll}
            slotProps={{ input: { "aria-label": "Select all versions" } }}
          />
          Version
        </span>
      ),
    },
    {
      id: "released",
      label: "Released",
      width: "120px",
      headerClassName: styles["header-cell"],
    },
    {
      id: "size",
      label: "Size",
      align: "right",
      width: "100px",
      headerClassName: styles["header-cell"],
    },
  ];

  return (
    <Card.Card>
      <Card.Title
        details={[`${versions.length} available from ${catalogName} catalog`]}
      >
        Versions
      </Card.Title>
      <DataTable
        chrome="plain"
        className={styles.table}
        columns={columns}
        empty={{ subject: emptySubject }}
      >
        {[
          ...versions.map((item, index) => {
            const cellClassName =
              index === versions.length - 1
                ? styles["last-row-cell"]
                : undefined;
            return (
              <Table.Row key={item.version}>
                <Table.Cell className={cellClassName}>
                  <FormControlLabel
                    className={styles["version-label"]}
                    slotProps={{
                      typography: { className: styles["version-text"] },
                    }}
                    label={
                      <LongTextLabel
                        label={<b>{item.version}</b>}
                        title={item.version}
                      />
                    }
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.includes(item.version)}
                        onChange={() => toggleVersion(item.version)}
                      />
                    }
                  />
                </Table.Cell>
                <Table.Cell className={cellClassName}>
                  <ValueOrPlaceholder
                    value={getReleaseDateDisplay(item.released)}
                  />
                </Table.Cell>
                <Table.Cell align="right" className={cellClassName}>
                  {humanReadableBytes(item.size, 2)}
                </Table.Cell>
              </Table.Row>
            );
          }),
          ...(versions.length > 0
            ? [
                <Table.Row key="summary">
                  <Table.Cell className={styles["footer-cell"]}>
                    <QuotaBar
                      variant="inline"
                      value={selectedBytes}
                      max={remainingCapacity}
                      label={{
                        tooltip: "of the remaining analysis capacity selected",
                        suffix: "of available capacity",
                      }}
                    />
                  </Table.Cell>
                  <Table.Cell
                    colSpan={2}
                    align="right"
                    className={getClassName(styles["footer-cell"], {
                      [styles["footer-error"]]: isOverCapacity,
                    })}
                  >
                    {isOverCapacity ? (
                      <b>
                        Exceeds capacity by{" "}
                        {humanReadableBytes(
                          selectedBytes - remainingCapacity,
                          2,
                        )}
                      </b>
                    ) : (
                      <>
                        <b>{selected.length} selected</b> &middot;{" "}
                        {humanReadableBytes(selectedBytes, 2)}
                      </>
                    )}
                  </Table.Cell>
                </Table.Row>,
              ]
            : []),
        ]}
      </DataTable>
    </Card.Card>
  );
};

export default ImportableVersionsSelection;
